import {Component, OnInit} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as moment from 'moment';
import {CallLogEntryModel} from '../../models/call-log-entry.model';
import {AbstractContactModel} from '../../models/abstract-contact.model';
import {ServiceNumberModel} from '../../models/service-number.model';
import {CallCauseString, DurationFormatter} from '../formatting.pipes';
import {GtAttendantService} from '../../services/gt-attendant.service';
import {GtAuthenticationService} from '@app/services/gt-authentication.service';
import {GtDirectoryService} from '@app/services/gt-directory.service';
import {BuilderFactory} from '../utils/builder.factory';
import {CallLogStringUtil} from '../utils/call-log-string-util';
import {CsvExport} from '../utils/csv-export.util';
import {GtServiceNumbersUtil} from '../utils/gt-service-numbers.util';
import {IStatisticsQueryOption, PeriodsOptionTypes, StatisticsQueryOptionTypes, TimeInterval} from './statistics-query-option';

@Component({
  selector: 'gt-swb-call-log',
  templateUrl: './call-log.component.html',
  styleUrls: ['./call-log.component.scss']
})
export class CallLogComponent implements OnInit {
  static dateFormat = 'YYYYMMDDHHmm';
  isAdmin = false;
  queryOption: IStatisticsQueryOption;
  selectedTimeInterval: TimeInterval;
  queryNumberOptions: IStatisticsQueryOption[];
  timeIntervals: TimeInterval[];
  logEntries: CallLogEntryModel[] = [];
  contacts: AbstractContactModel[] = null;
  StatisticsQueryOptionTypes = StatisticsQueryOptionTypes;

  constructor(
    private authenticationService: GtAuthenticationService,
    private directoryService: GtDirectoryService,
    private translateService: TranslateService,
    private attendantService: GtAttendantService,
    private durationFormatter: DurationFormatter,
    private callCauseStringPipe: CallCauseString
  ) {}

  ngOnInit() {
    this.initTimeInterval();

    this.authenticationService.isAdmin().subscribe(isAdmin => {
      this.isAdmin = isAdmin;
      this.authenticationService.isloggedIn().subscribe(loggedIn => {
        if (loggedIn) {
          this.directoryService.getContacts(this.authenticationService.oname, false).subscribe(contacts => {
            this.contacts = contacts;
            this.initQueryOptions();
          });
        }
      });
    });
  }

  retrieveCallLog() {
    const queryOption = this.queryOption;
    const selectedTimeInterval = this.selectedTimeInterval;
    if (queryOption && selectedTimeInterval) {
      localStorage.callLogNumber = queryOption.fullNumber;
      if (queryOption.type === StatisticsQueryOptionTypes.SERVICENUMBER) {
        this.attendantService
          .getServiceNumberCallLog(
            queryOption.fullNumber,
            this.authenticationService.oname,
            this.authenticationService.login,
            selectedTimeInterval.start,
            selectedTimeInterval.end,
            this.contacts
          )
          .subscribe(
            callLog => {
              this.populateCallLogEntry(callLog);
            },
            () => (this.logEntries = null)
          );
      } else {
        this.attendantService
          .getAgentCallLog(
            this.authenticationService.oname,
            this.authenticationService.login,
            selectedTimeInterval.start,
            selectedTimeInterval.end,
            this.contacts
          )
          .subscribe(
            callLog => {
              this.populateCallLogEntry(callLog);
            },
            () => (this.logEntries = null)
          );
      }
    }
  }

  private populateCallLogEntry(callLog: CallLogEntryModel[]) {
    if (callLog) {
      this.logEntries = callLog.map(log => {
        const matchingContact = this.contacts.find(x => x.phoneNumber === log.fullNumber);
        if (matchingContact) {
          log.caller = matchingContact.name;
        }
        return log;
      });
    } else {
      this.logEntries = null;
    }
  }

  exportToCSV() {
    const rows = [];
    const columnHeaders = [
      'call_log.col_time',
      'call_log.col_status',
      'call_log.col_caller',
      'call_log.col_duration',
      'call_log.col_access_number',
      'call_log.col_answered_by',
      'call_log.col_history'
    ].map(k => this.translateService.instant(k));
    const header = columnHeaders.join(';');
    rows.push(header);

    this.logEntries.forEach((logEntry: CallLogEntryModel) => {
      const columns = [];
      const startTime = moment(logEntry.callStart).format(moment.localeData().longDateFormat('LLL').replace('YYYY ', ''));
      columns.push(startTime);
      columns.push(this.callCauseStringPipe.transform(logEntry));
      const contactPrettifiedName = CallLogStringUtil.getDisplayNumber(logEntry.caller, logEntry.fullNumber);
      columns.push(contactPrettifiedName);

      columns.push(this.durationFormatter.transform(logEntry.duration));
      columns.push(logEntry.accessNumber);
      columns.push(logEntry.answeredBy);
      columns.push(this.getCallHistory(logEntry));
      rows.push(columns.join(';'));
    });
    const data = rows.join('\n');
    CsvExport.downloadCSV(data, 'call-log.csv');
  }

  private getPrettifiedNameByLogin(login: string): string {
    const loginContact = this.contacts ? this.contacts.find(contact => login === contact.login) : null;
    return loginContact ? loginContact.name : '';
  }

  private getCallHistory(logEntry: CallLogEntryModel): string {
    const displayNames = logEntry.redirectEntries.map(event => {
      const prettifiedName = this.getPrettifiedNameByLogin(event.phonenumber);
      return CallLogStringUtil.getDisplayNumber(prettifiedName, event.phonenumber);
    });
    return displayNames.join(' > ');
  }

  // Today
  // Yesterday
  // Two days ago
  // Last 7 days
  // Last 30 days
  private initTimeInterval() {
    this.timeIntervals = [{
      name: PeriodsOptionTypes.Today,
      start: moment().startOf('day').format(CallLogComponent.dateFormat),
      end: moment().endOf('day').format(CallLogComponent.dateFormat),
      today: true
    }, {
      name: PeriodsOptionTypes.Yesterday,
      start: moment().subtract(1, 'day').startOf('day').format(CallLogComponent.dateFormat),
      end: moment().subtract(1, 'day').endOf('day').format(CallLogComponent.dateFormat),
      today: true
    }, {
      name: PeriodsOptionTypes.TwoDaysAgo,
      start: moment().subtract(2, 'day').startOf('day').format(CallLogComponent.dateFormat),
      end: moment().subtract(2, 'day').endOf('day').format(CallLogComponent.dateFormat),
      today: true
    }, {
      name: PeriodsOptionTypes.Last7Days,
      start: moment().subtract(7, 'day').startOf('day').format(CallLogComponent.dateFormat),
      end: moment().endOf('day').format(CallLogComponent.dateFormat),
      today: true
    }, {
      name: PeriodsOptionTypes.Last30Days,
      start: moment().subtract(30, 'day').startOf('day').format(CallLogComponent.dateFormat),
      end: moment().endOf('day').format(CallLogComponent.dateFormat),
      today: true
    }]
    this.selectedTimeInterval = this.timeIntervals[0];
  }

  private initQueryOptions(): void {
    this.queryNumberOptions = [];

    const name = CallLogStringUtil.getPrettifyName(
      this.authenticationService.userData.firstName,
      this.authenticationService.userData.lastName
    );
    this.queryNumberOptions.push(
      this.createQueryOption(StatisticsQueryOptionTypes.AGENT, name, this.authenticationService.userData.fullNumber)
    );

    this.directoryService.getServiceNumberModels(this.authenticationService.oname, false).subscribe(serviceNumbers => {
      this.handleServiceNumbers(serviceNumbers);
    });
  }

  private handleServiceNumbers(serviceNumbers: ServiceNumberModel[]) {
    serviceNumbers.forEach(serviceNumber => {
      if (
        GtServiceNumbersUtil.visibleInCallLog(serviceNumber) &&
        serviceNumber.hasSkill(this.authenticationService.oname, this.authenticationService.login)
      ) {
        this.queryNumberOptions.push(
          this.createQueryOption(StatisticsQueryOptionTypes.SERVICENUMBER, serviceNumber.name, serviceNumber.phoneNumber)
        );
      }
    });

    const callLogNumber: string = localStorage.getItem('callLogNumber');
    this.queryOption = this.queryNumberOptions.find(queryNumberOption => {
      return queryNumberOption.fullNumber === callLogNumber;
    });
    if (this.queryOption === undefined) {
      this.queryOption = this.queryNumberOptions[0];
    }

    this.retrieveCallLog();
  }

  private createQueryOption(optionType: StatisticsQueryOptionTypes, name: string, fullNumber: string) {
    const builder = BuilderFactory.createBuilder<IStatisticsQueryOption>();
    const option = builder.type(optionType).name(name).label(CallLogStringUtil.getDisplayNumber(name, fullNumber)).fullNumber(fullNumber);
    return option.build();
  }
}
